import {
  Button,
  Divider,
  Grid,
  Paper,
  Typography,
  Backdrop,
  CircularProgress,
  List,
  Stack,
  Tooltip,
} from "@mui/material";
import {
  DriveFolderUpload as DriveFolderUploadIcon,
  Save,
  Settings,
  SettingsApplications,
  Download,
  ViewList as ViewListIcon,
  Group as GroupIcon,
} from "@mui/icons-material";
import { useState, useEffect, useCallback, useContext, useRef } from "react";
import EditTabSourceModal from "./EditTabSourceModal";
import {
  emptyTag,
  updateDeviceParamsFormat,
  lookupNameToId,
  lookupIdToName,
  matchesSourceType,
  parseHwioChannelNumber,
  saveChangesTooltipText,
  addLookupValueFieldsToTag,
  getMaxTagIdentifier,
  getTagsWithUnresolvedParent,
  validateTagIdUniqueness,
  generateSafeAlarmIds,
  readArrayFromStorage,
  parseJsonObject,
  coerceToBoolean,
} from "../helpers/Helpers";
import { isColumnCompatibleWithTag } from "../helpers/SourceTypeColumns";
import {
  getTagListById,
  saveTags,
  saveTagLayers,
  getTagsWithFilter,
  queueTagSync,
} from "../../api/APIHelpers";
import _ from "lodash";
import "./EditTabComponent.css";
import { useParams } from "react-router-dom";
import SourceSelection from "./SourceSelection";
import ConfirmDialog from "../../common/components/ConfirmDialog";
import TagDeployer from "./DeployGrid";
import { SharedTagDialog } from "./SharedTagDialog";
import { PreSaveWarningsContent } from "./PreSaveWarningsContent";
import { ConfigureTagList } from "./ConfigureTagList";
import { CollisionMode, SelectCollisionMode } from "./SelectCollisionMode";
import TagComparisonModal from "./TagComparison/TagComparisonModal";
import LoadXMLModal from "./LoadXMLModal";
import { XMLParser } from "fast-xml-parser";
import XMLBuilder from "fast-xml-builder";
import { LookupValueContext } from "../../common/contexts/LookupValueContext";
import {
  createTextColumn,
  visibleColumnOrderStorageKey,
  visibleColumnsStorageKey,
} from "../../common/components/DataGrid/DataGridHelper";
import {
  insertElement,
  deleteElement,
  updateIndexList,
} from "../../common/helpers/Utils";
import { ManageTagListsModal } from "./ManageTagListsModal";
import TagDataGridWrapper from "./TagDataGridWrapper";
import SelectTagStandard from "./SelectTagStandard";
import localforage from "localforage";
import { AppendToTagList } from "./AppendToTagList";
import BulkUpdateModal from "./BulkUpdateModal";
import GroupPane from "./GroupPane";
import { AlertsContext } from "../../common/contexts/AlertsContext";
import { extractApiErrorMessage } from "../../common/helpers/ErrorUtils";

if (localforage.supports(localforage.INDEXEDDB)) {
  localforage.config({
    driver: localforage.INDEXEDDB,
    name: "Tag Configurator",
  });
}

export default function EditTabComponent() {
  const [openSourceModal, setOpenSourceModal] = useState(false);
  const [openManageTagListsModal, setOpenManageTagListsModal] = useState(false);
  const [openXMLModal, setOpenXMLModal] = useState(false);
  const [tags, setTags] = useState([]); // Original tag records, used to determine which fields have been edited, does not show in the grid
  const [editedTags, setEditedTags] = useState([]); // Tag records that are actually showing in the grid
  const [selectedTags, setSelectedTags] = useState(new Set()); // Which rows are selected
  const [dirtyTags, setDirtyTags] = useState(new Set()); // List of tag indexes that have been modified
  const [loading, setLoading] = useState(false);
  const [sourceFilter, setSourceFilter] = useState([]);
  const { assetIds } = useParams();
  const { setInfoMessages } = useContext(AlertsContext);
  const [targetAssets, setTargetAssets] = useState([]);
  const [newTagList, setNewTagList] = useState({}); // Information about new tag list to save grid into
  const [assetTargetFilter, setAssetTargetFilter] = useState([]); // Parameters to query for assets to target for deployment

  // Confirmation window
  const [showConfirmDeployDialog, setShowConfirmDeployDialog] = useState(false);
  const [confirmationOnClose, setConfirmationOnClose] = useState(null);
  const [confirmationTitle, setConfirmationTitle] = useState("Confirm");
  const [confirmationText, setConfirmationText] = useState("Confirm");

  const [showDeployGrid, setShowDeployGrid] = useState(false);
  const [tagsForDeploy, setTagsForDeploy] = useState([]);

  const [tagListConfigOpen, setTagListConfigOpen] = useState(false);
  const [tagListAppendOpen, setTagListAppendOpen] = useState(false);
  const [bulkUpdateModalOpen, setBulkUpdateModalOpen] = useState(false);
  const [currentView, setCurrentView] = useState("grid");

  const [distro, setDistro] = useState("2.7.4");

  const [showTagComparisonDialog, setShowTagComparisonDialog] = useState(false);

  const lookupValues = useContext(LookupValueContext);

  const initialLoad = useRef(true);

  useEffect(() => {
    if (!initialLoad.current) {
      const setStorageAsync = async () => {
        try {
          await localforage.setItem("tags", tags);
          await localforage.setItem("editedTags", editedTags);
          await localforage.setItem("dirtyTags", dirtyTags);
          await localforage.setItem("selectedTags", selectedTags);
          await localforage.setItem("sourceFilter", sourceFilter);
          await localforage.setItem("assetTargetFilter", assetTargetFilter);
        } catch (e) {
          console.error("Error storing grid session in browser");
        }
      };
      setStorageAsync();
    }
  }, [
    dirtyTags,
    editedTags,
    selectedTags,
    tags,
    sourceFilter,
    assetTargetFilter,
  ]);

  useEffect(() => {
    const loadStorageAsync = async () => {
      try {
        const loadedTags = await localforage.getItem("tags");
        const loadedEditedTags = await localforage.getItem("editedTags");
        const loadedDirtyTags = await localforage.getItem("dirtyTags");
        const loadedSelectedTags = await localforage.getItem("selectedTags");
        const loadedSourceFilter = await localforage.getItem("sourceFilter");
        const loadedAssetTargetFilter =
          await localforage.getItem("assetTargetFilter");
        setTags(loadedTags || []);
        setEditedTags(loadedEditedTags || []);
        setDirtyTags(loadedDirtyTags || new Set());
        setSelectedTags(loadedSelectedTags || new Set());
        setSourceFilter(loadedSourceFilter || []);
        setAssetTargetFilter(loadedAssetTargetFilter || []);
        initialLoad.current = false;
      } catch (e) {
        console.info("No cached grid session found.");
        initialLoad.current = false;
      }
    };
    loadStorageAsync();
  }, []);

  // Tag ID collision settings
  const [collisionMode, setCollisionMode] = useState(
    localStorage.getItem("collisionMode") || CollisionMode.NoneSet
  );
  const [selectedColumns, setSelectedColumns] = useState(() =>
    readArrayFromStorage(visibleColumnsStorageKey)
  );

  const [tagStandard, setTagStandard] = useState(
    localStorage.getItem("tagStandard") || -1
  );
  const [tagStandardTags, setTagStandardTags] = useState([]);

  const confirmationDialog = (
    <ConfirmDialog
      open={showConfirmDeployDialog}
      onClose={confirmationOnClose}
      title={confirmationTitle}
      content={confirmationText}
    />
  );

  const appendTagsToGrid = useCallback(
    (tagsAdded) => {
      let newEditedTags = [...editedTags];
      let newTags = [...tags];

      for (const tag of tagsAdded) {
        // Check for tagid collision
        const collisionIndex = editedTags.findIndex(
          (t) =>
            t.TagIdentifier &&
            tag.TagIdentifier &&
            t.TagIdentifier.toString() === tag.TagIdentifier.toString()
        );
        if (collisionIndex !== -1) {
          // Handle collision
          if (collisionMode === CollisionMode.Overwrite) {
            // Replace old tag with new tag
            newEditedTags.splice(collisionIndex, 1, tag);
            newTags.splice(collisionIndex, 1, tag);
          } else if (collisionMode === CollisionMode.Ignore) {
            // Insert as normal
            insertElement(newEditedTags, tag, newEditedTags.length);
            insertElement(newTags, tag, newTags.length);
          }
        } else {
          // Insert as normal
          insertElement(newEditedTags, tag, newEditedTags.length);
          insertElement(newTags, tag, newTags.length);
        }
      }
      // Update state
      setEditedTags(newEditedTags);
      setTags(newTags);
    },
    [collisionMode, editedTags, tags]
  );

  const addNewEmptyTag = (at) => {
    const newEmptyTag = { ...emptyTag };
    let newEditedTags = [...editedTags];
    let newTags = [...tags];
    insertElement(newEditedTags, newEmptyTag, at);
    insertElement(newTags, newEmptyTag, at);
    setEditedTags(newEditedTags);
    setTags(newTags);
    setDirtyTags(updateIndexList(dirtyTags, at, 1));
  };

  const duplicateTag = (at) => {
    let dupe = { ...editedTags[at] };

    // Clear out fields that link the new duplicate to an existing tag or asset in the DB
    if (dupe.TagId) {
      delete dupe.TagId;
    }
    if (dupe.TCTagLayerId) {
      delete dupe.TCTagLayerId;
    }
    if (dupe.AssetId) {
      delete dupe.AssetId;
    }
    if (dupe.AssetNumber) {
      delete dupe.AssetNumber;
    }

    const newTagIdentifier = getMaxTagIdentifier(editedTags) + 1;
    dupe.TagIdentifier = newTagIdentifier;
    const { safeAlarmId, safeAlarm2Id } = generateSafeAlarmIds(
      dupe,
      editedTags,
      newTagIdentifier
    );
    dupe.AlarmId = safeAlarmId;
    dupe.Alarm2Id = safeAlarm2Id;

    let newEditedTags = [...editedTags];
    let newTags = [...tags];
    insertElement(newEditedTags, dupe, at);
    insertElement(newTags, dupe, at);
    setEditedTags(newEditedTags);
    setTags(newTags);
    setDirtyTags(updateIndexList(dirtyTags, at + 1, 1));
  };

  const removeRowAtIndex = (rowId, nextEditedTags, nextTags, nextDirtyTags) => {
    deleteElement(nextEditedTags, rowId);
    deleteElement(nextTags, rowId);

    const updatedDirtyTags = new Set(nextDirtyTags);
    if (updatedDirtyTags.has(rowId)) {
      updatedDirtyTags.delete(rowId);
    }

    return updateIndexList(updatedDirtyTags, rowId, -1);
  };

  const deleteRow = (id) => {
    console.info("deleting row " + id);
    let newEditedTags = [...editedTags];
    let newTags = [...tags];
    let newDirtyTags = removeRowAtIndex(id, newEditedTags, newTags, dirtyTags);
    setEditedTags(newEditedTags);
    setTags(newTags);
    setDirtyTags(newDirtyTags);
  };

  const deleteSelectedRows = (rowsToDelete) => {
    const selectedRowIds = Array.from(rowsToDelete || []).filter(
      (rowId) =>
        Number.isInteger(rowId) && rowId >= 0 && rowId < editedTags.length
    );

    if (selectedRowIds.length === 0) {
      return;
    }

    const sortedRowIds = selectedRowIds.sort((a, b) => b - a);
    let newEditedTags = [...editedTags];
    let newTags = [...tags];
    let newDirtyTags = new Set(dirtyTags);

    sortedRowIds.forEach((rowId) => {
      newDirtyTags = removeRowAtIndex(
        rowId,
        newEditedTags,
        newTags,
        newDirtyTags
      );
    });

    setEditedTags(newEditedTags);
    setTags(newTags);
    setDirtyTags(newDirtyTags);
    setSelectedTags(new Set());
  };

  const bulkUpdateTags = (selectedIndices, fieldInfo, newValue) => {
    const fieldName = fieldInfo?.key;
    let newEditedTags = [...editedTags];
    let newDirtyTags = new Set(dirtyTags);

    const modbusFields = {
      ModbusRegister: {
        getValue: (deviceParams) => deviceParams?.regnum,
        setValue: (deviceParams, value) => ({ ...deviceParams, regnum: value }),
      },
      ModbusIP: {
        getValue: (deviceParams) => deviceParams?.connection?.ip,
        setValue: (deviceParams, value) => ({
          ...deviceParams,
          connection: { ...deviceParams?.connection, ip: value },
        }),
      },
      ModbusPort: {
        getValue: (deviceParams) => deviceParams?.connection?.port,
        setValue: (deviceParams, value) => ({
          ...deviceParams,
          connection: { ...deviceParams?.connection, port: value },
        }),
      },
      ModbusPortRTU: {
        getValue: (deviceParams) => deviceParams?.connection?.port_number,
        setValue: (deviceParams, value) => ({
          ...deviceParams,
          connection: { ...deviceParams?.connection, port_number: value },
        }),
      },
      ModbusSlaveId: {
        getValue: (deviceParams) => deviceParams?.connection?.slave_id,
        setValue: (deviceParams, value) => ({
          ...deviceParams,
          connection: { ...deviceParams?.connection, slave_id: value },
        }),
      },
      LoSetpoint: {
        getValue: (deviceParams) =>
          deviceParams?.properties_to_sync?.properties?.LoLimit?.regnum,
        setValue: (deviceParams, value) => ({
          ...deviceParams,
          properties_to_sync: {
            ...deviceParams?.properties_to_sync,
            properties: {
              ...deviceParams?.properties_to_sync?.properties,
              LoLimit: {
                ...deviceParams?.properties_to_sync?.properties?.LoLimit,
                regnum: value,
              },
            },
          },
        }),
      },
      LoLoSetpoint: {
        getValue: (deviceParams) =>
          deviceParams?.properties_to_sync?.properties?.LoLoLimit?.regnum,
        setValue: (deviceParams, value) => ({
          ...deviceParams,
          properties_to_sync: {
            ...deviceParams?.properties_to_sync,
            properties: {
              ...deviceParams?.properties_to_sync?.properties,
              LoLoLimit: {
                ...deviceParams?.properties_to_sync?.properties?.LoLoLimit,
                regnum: value,
              },
            },
          },
        }),
      },
      HiSetpoint: {
        getValue: (deviceParams) =>
          deviceParams?.properties_to_sync?.properties?.HiLimit?.regnum,
        setValue: (deviceParams, value) => ({
          ...deviceParams,
          properties_to_sync: {
            ...deviceParams?.properties_to_sync,
            properties: {
              ...deviceParams?.properties_to_sync?.properties,
              HiLimit: {
                ...deviceParams?.properties_to_sync?.properties?.HiLimit,
                regnum: value,
              },
            },
          },
        }),
      },
      HiHiSetpoint: {
        getValue: (deviceParams) =>
          deviceParams?.properties_to_sync?.properties?.HiHiLimit?.regnum,
        setValue: (deviceParams, value) => ({
          ...deviceParams,
          properties_to_sync: {
            ...deviceParams?.properties_to_sync,
            properties: {
              ...deviceParams?.properties_to_sync?.properties,
              HiHiLimit: {
                ...deviceParams?.properties_to_sync?.properties?.HiHiLimit,
                regnum: value,
              },
            },
          },
        }),
      },
      SyncDirection: {
        getValue: (deviceParams) =>
          deviceParams?.properties_to_sync?.sync_direction,
        setValue: (deviceParams, value) => ({
          ...deviceParams,
          properties_to_sync: {
            ...deviceParams?.properties_to_sync,
            sync_direction: value,
          },
        }),
      },
    };

    const lookupFieldMappings = {
      TagSourceTypeId: { stringField: "SourceType", category: "TagSourceType" },
      AlarmTypeId: { stringField: "AlarmType", category: "TagAlarmType" },
      DataTypeId: { stringField: "DataType", category: "TagDataType" },
      AlarmLimitRangeId: {
        stringField: "AlarmLimitRange",
        category: "AlarmLimitRange",
      },
      AlarmSeverityId: {
        stringField: "AlarmSeverity",
        category: "AlarmSeverity",
      },
      AlarmArmingTypeId: {
        stringField: "AlarmArmingType",
        category: "AlarmArmingType",
      },
    };

    // HardWiredIO fields live directly on the tag record rather than in DeviceParams. Each entry
    // converts the bulk value into the form the underlying field stores.
    const hwioFields = {
      TagTypeId: { setValue: (value) => parseInt(value) },
      // Channel numbers are entered 1-based and may carry an I/O type prefix ("AI 5") to match the
      // grid and the shared tag editor; the prefix is ignored and the value is stored 0-based.
      ChannelNumber: { setValue: (value) => parseHwioChannelNumber(value) - 1 },
      PortNumber: { setValue: (value) => parseInt(value) },
    };

    /**
     * Whether a field applies to a tag's source type. Restricted fields name the grid column whose
     * rule they share, so the source types themselves live only in SourceTypeColumns.js.
     */
    const isTagCompatibleWithField = (tag) => {
      if (fieldInfo?.columnId) {
        return isColumnCompatibleWithTag(fieldInfo.columnId, tag, lookupValues);
      }
      if (fieldInfo?.sourceTypes?.length) {
        return matchesSourceType(tag, fieldInfo.sourceTypes, lookupValues);
      }
      return true;
    };

    let updatedCount = 0;
    let skippedCount = 0;

    selectedIndices.forEach((index) => {
      const tag = newEditedTags[index];

      // Guard before writing anything: incompatible tags are skipped entirely, never written and
      // never marked dirty.
      if (!isTagCompatibleWithField(tag)) {
        skippedCount++;
        return;
      }

      if (hwioFields[fieldName]) {
        const storedValue = hwioFields[fieldName].setValue(newValue);
        if (Number.isNaN(storedValue)) {
          console.warn(
            `Skipping tag ${index} - "${newValue}" is not a valid ${fieldName}`
          );
          skippedCount++;
          return;
        }

        newEditedTags[index] = { ...tag, [fieldName]: storedValue };
        newDirtyTags.add(index);
        updatedCount++;
        return;
      }

      if (modbusFields[fieldName]) {
        const modbusField = modbusFields[fieldName];

        try {
          let deviceParams = {};
          if (tag.DeviceParams) {
            try {
              deviceParams = JSON.parse(tag.DeviceParams);
            } catch (e) {
              console.warn(
                `Invalid DeviceParams for tag ${index}, creating new structure`
              );
              deviceParams = { connection: {} };
            }
          } else {
            deviceParams = { connection: {} };
          }

          const updatedDeviceParams = modbusField.setValue(
            deviceParams,
            newValue
          );

          newEditedTags[index] = {
            ...tag,
            DeviceParams: JSON.stringify(updatedDeviceParams),
          };
        } catch (error) {
          console.error(`Error updating DeviceParams for tag ${index}:`, error);
          skippedCount++;
          return;
        }
      } else {
        if (fieldName === "Hidden") {
          const dynamicProperties = parseJsonObject(tag.DynamicProperties, {});
          dynamicProperties.isHidden = coerceToBoolean(newValue);
          const updatedTag = {
            ...tag,
            DynamicProperties: JSON.stringify(dynamicProperties),
          };
          delete updatedTag.Hidden;
          newEditedTags[index] = updatedTag;
          newDirtyTags.add(index);
          updatedCount++;
          return;
        }

        if (fieldName === "SafetyShutdown") {
          const dynamicProperties = parseJsonObject(tag.DynamicProperties, {});
          const flags = dynamicProperties.flags || {};
          const isEnabled = coerceToBoolean(newValue);

          if (isEnabled) {
            flags["Safety Shutdown"] = true;
          } else {
            delete flags["Safety Shutdown"];
          }

          if (Object.keys(flags).length > 0) {
            dynamicProperties.flags = flags;
          } else {
            delete dynamicProperties.flags;
          }

          const updatedTag = {
            ...tag,
            DynamicProperties: JSON.stringify(dynamicProperties),
          };
          delete updatedTag.SafetyShutdown;
          newEditedTags[index] = updatedTag;
          newDirtyTags.add(index);
          updatedCount++;
          return;
        }

        // Special handling for AlarmId fields
        let valueToSet = newValue;
        if (fieldName === "AlarmId" && newValue === "__TAG_IDENTIFIER__") {
          valueToSet = parseInt(tag.TagIdentifier);
        } else if (
          fieldName === "Alarm2Id" &&
          newValue === "5+__TAG_IDENTIFIER__"
        ) {
          valueToSet = parseInt("5" + tag.TagIdentifier.toString());
        } else if (fieldInfo?.type === "bool") {
          valueToSet = coerceToBoolean(newValue);
        }

        const updatedTag = {
          ...tag,
          [fieldName]: valueToSet,
        };

        if (lookupFieldMappings[fieldName]) {
          const mapping = lookupFieldMappings[fieldName];
          const stringValue = lookupIdToName(
            valueToSet,
            mapping.category,
            lookupValues
          );
          if (stringValue) {
            updatedTag[mapping.stringField] = stringValue;
          }
        }

        newEditedTags[index] = updatedTag;
      }

      newDirtyTags.add(index);
      updatedCount++;
    });

    setEditedTags(newEditedTags);
    setDirtyTags(newDirtyTags);

    const fieldLabel = fieldInfo?.name ?? fieldName;
    const skippedSuffix =
      skippedCount > 0
        ? ` (${skippedCount} skipped — incompatible source type)`
        : "";
    setInfoMessages({
      type: "snackbar",
      message: `Updated ${fieldLabel} for ${updatedCount} tag${updatedCount !== 1 ? "s" : ""}${skippedSuffix}`,
      severity: updatedCount > 0 ? "success" : "warning",
      open: true,
    });
  };

  const [dialogTag, setDialogTag] = useState({});
  const [dialogId, setDialogId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const openTagDialog = (id) => {
    if (dialogOpen) return;
    setDialogTag(editedTags[id]);
    setDialogId(id);
    setDialogOpen(true);
  };

  useEffect(() => {
    if (assetIds && Array.isArray(lookupValues) && lookupValues.length > 0) {
      localforage.removeItem("tags");
      localforage.removeItem("editedTags");
      localforage.removeItem("dirtyTags");
      localforage.removeItem("selectedTags");
      localforage.removeItem("sourceFilter");
      localforage.removeItem("assetTargetFilter");
      clearGrid();

      const assetIdFilter = assetIds.split(",").map((item, idx, ogArr) => {
        return {
          rowId: idx,
          operator: "AND",
          fieldName: "AssetId",
          comparator: "=",
          value: item.trim(),
          addButton: ogArr.length - 1 === idx,
          fieldSource: "Tag",
        };
      });
      setSourceFilter(assetIdFilter);
      const getURLTags = async () => {
        setLoading(true);
        let result = await getTagsWithFilter(assetIdFilter);
        const tags = result.data.map((tag) => {
          return addLookupValueFieldsToTag(tag, lookupValues);
        });
        appendTagsToGrid(tags);
        setLoading(false);
      };
      getURLTags();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetIds, lookupValues]);

  useEffect(() => {
    let newTagsForDeploy = _.filter(editedTags, (_, index) =>
      selectedTags.has(index)
    );
    setTagsForDeploy(newTagsForDeploy);
  }, [selectedTags, editedTags]);

  useEffect(() => {
    const getTagStandardTags = async () => {
      try {
        const tagst = await getTagListById(tagStandard);
        setTagStandardTags(tagst.data.Tags);
      } catch (error) {
        console.error("Failed to get tag standard tags:", error);
        setInfoMessages({
          type: "snackbar",
          message: `Failed to get tag standard tags: ${extractApiErrorMessage(error)}`,
          severity: "error",
          open: true,
        });
      }
    };
    if (tagStandard !== -1) {
      getTagStandardTags();
    }
  }, [tagStandard, setInfoMessages]);

  const collisionPrompt = () => {
    const closePrompt = async (_) => {
      setShowConfirmDeployDialog(false);
    };
    setConfirmationOnClose(() => closePrompt);
    setConfirmationTitle("Tag ID Collisions");
    setConfirmationText(
      <>
        {
          "You do not have a Tag ID Collision mode selected. Please select one before loading tags into the grid: "
        }
        <List>
          <li>
            <b>Ignore</b> - Tags will be appended to the grid with no action
            taken
          </li>
          <li>
            <b>Overwrite</b> - Tags with a tag ID collision will be overwritten
            with the loaded tag(s)
          </li>
          <li>
            <b>Skip</b> - Do not load tag IDs with a collision into the grid
          </li>
        </List>
      </>
    );
    setShowConfirmDeployDialog(true);
  };

  const updateTagFromEditor = (row) => {
    let tagToUpdate = row.id;
    let newEditedTags = [...editedTags];
    newEditedTags[tagToUpdate] = row;
    setEditedTags(newEditedTags);

    let isDirty = dirtyTags.has(tagToUpdate);
    if (!_.isEqual(row, tags[tagToUpdate])) {
      if (!isDirty) {
        let newDirtyTags = new Set(dirtyTags);
        newDirtyTags.add(tagToUpdate);
        setDirtyTags(newDirtyTags);
      }
    } else {
      if (isDirty) {
        let newDirtyTags = new Set(dirtyTags);
        newDirtyTags.delete(tagToUpdate);
        setDirtyTags(newDirtyTags);
      }
    }
    return row;
  };

  const getTagsToSave = () => {
    let tagsToSave = [];
    let selectedDirtyTags = _.filter(Array.from(dirtyTags), (i) =>
      selectedTags.has(i)
    );
    for (const tagIndex of selectedDirtyTags) {
      if (
        editedTags[tagIndex].AssetTagId ||
        editedTags[tagIndex].TCTagLayerId
      ) {
        tagsToSave.push(editedTags[tagIndex]);
      }
    }
    return tagsToSave;
  };

  const getTagLists = async (selectedTagLists) => {
    try {
      if (collisionMode === CollisionMode.NoneSet) {
        collisionPrompt();
        throw new Error("Collision mode is not set.");
      }
      if (!selectedTagLists || selectedTagLists.length === 0) {
        setInfoMessages({
          type: "snackbar",
          message: "Please select at least one tag list.",
          severity: "warning",
          open: true,
        });
        return;
      }
      setLoading(true);
      let tagListTags = [];
      for (const tagListInfo of selectedTagLists) {
        const tagListRecord = await getTagListById(tagListInfo.TCTagListId);
        for (const tag of tagListRecord.data.Tags) {
          let modifiedTagLayer = {
            ...tag,
            TagListName: tagListInfo.Name,
          };
          tagListTags.push(
            addLookupValueFieldsToTag(modifiedTagLayer, lookupValues)
          );
        }
      }
      appendTagsToGrid(tagListTags);
      const confirmNotice = {
        open: true,
        severity: "success",
        message: "Successfully appended tag list(s) to grid.",
      };
      setInfoMessages(confirmNotice);
    } catch (error) {
      console.error("Failed to get tag lists:", error);
      setInfoMessages({
        type: "snackbar",
        message: `Failed to get tag lists: ${extractApiErrorMessage(error)}`,
        severity: "error",
        open: true,
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const constructNewTagListFromCurrentGrid = (newTagList) => {
    let tagsToSave = _.filter(editedTags, (_, index) =>
      selectedTags.has(index)
    );
    let tagList = { ...newTagList };
    let Tags = [];
    for (const record of tagsToSave) {
      let tag = { ...record };
      if (tag.TagId) {
        delete tag.TagId;
      }
      if (tag.TCTagLayerId) {
        delete tag.TCTagLayerId;
      }
      if (tag.SourceType && !tag.TagSourceTypeId) {
        tag.TagSourceTypeId = lookupNameToId(
          tag.SourceType,
          "TagSourceType",
          lookupValues
        );
      }
      Tags.push(tag);
    }
    return [tagList, Tags];
  };

  // Executes when the final save button is clicked after a save target has been selected
  const saveChanges = async () => {
    const tagsToSave = getTagsToSave();
    if (tagsToSave.length === 0) {
      setInfoMessages({
        type: "snackbar",
        message:
          'No tags were saved. Changes must be made and tags must be selected. New tags must be deployed with the "Select Assets" button.',
        severity: "warning",
        open: true,
        autoHideDuration: 10000,
      });
      return;
    }

    // Save tags & tag layers to the source records
    const proceedWithSave = async () => {
      setInfoMessages({
        type: "snackbar",
        severity: "info",
        message: "Tag changes have been queued for save.",
        open: true,
      });
      const assetIds = tagsToSave
        .filter((t) => t.AssetId != undefined)
        .map((t) => t.AssetId);
      try {
        validateTagIdUniqueness(editedTags);
        const tagRecords = tagsToSave.filter((t) => t.AssetTagId);
        const res = await saveTags(tagRecords);

        const tagLayers = tagsToSave.filter((t) => t.TCTagLayerId);
        const tagLayerRes = await saveTagLayers(tagLayers);

        // Pair each save attempt with the tag object that produced it (Promise.allSettled preserves
        // input order), so a failure can be traced back to the real tag instead of re-parsing the
        // outgoing request body.
        const attempts = [
          ...tagRecords.map((tag, index) => ({ tag, result: res[index] })),
          ...tagLayers.map((tag, index) => ({
            tag,
            result: tagLayerRes[index],
          })),
        ];
        const failedAttempts = attempts.filter(
          (attempt) => attempt.result.status === "rejected"
        );
        const successCount = attempts.length - failedAttempts.length;

        const failedTagDetails = failedAttempts.map(({ tag, result }) => ({
          tag,
          label: tag.TagName
            ? `${tag.TagName} (${tag.TagIdentifier})`
            : `TCTagLayerId: ${tag.TCTagLayerId}`,
          reason: extractApiErrorMessage(result.reason),
        }));

        if (failedTagDetails.length > 0) {
          if (successCount === 0 && failedTagDetails.length === 1) {
            setInfoMessages({
              message: `Failed to save tag: ${failedTagDetails[0].reason}`,
              severity: "error",
              type: "snackbar",
              open: true,
              autoHideDuration: 10000,
            });
          } else {
            const outStr = failedTagDetails
              .map(
                (detail) => `${detail.label}, Reason: ${detail.reason} <br />`
              )
              .join("");
            const introText =
              successCount === 0
                ? "There was an error saving your changes. The following tags did not update properly:"
                : "There was an error saving some of your changes. The following tags did not update properly:";
            setInfoMessages({
              type: "dialog",
              message: (
                <div>
                  {introText}
                  <br />
                  {outStr}
                </div>
              ),
              title: "Save Errors",
              open: true,
            });
          }
        } else {
          setInfoMessages({
            type: "snackbar",
            message: "Tags saved successfully.",
            severity: "success",
            open: true,
          });
        }

        // Reconcile grid state whenever at least one tag in this batch actually saved: rows that
        // were part of the batch and succeeded get their saved baseline updated and are no longer
        // dirty; rows that failed (or weren't part of this batch) remain dirty and untouched. When
        // every attempted tag fails, skip this entirely so the grid state is left exactly as-is.
        if (successCount > 0) {
          const failedTagRefs = new Set(
            failedTagDetails.map((detail) => detail.tag)
          );
          let selectedTagChanges = [...editedTags];
          let newTags = [...tags];
          let remainingDirtyTags = new Set();
          for (const tagIndex of dirtyTags) {
            if (
              !selectedTags.has(tagIndex) ||
              failedTagRefs.has(editedTags[tagIndex])
            ) {
              selectedTagChanges[tagIndex] = editedTags[tagIndex];
              remainingDirtyTags.add(tagIndex);
            } else {
              newTags[tagIndex] = editedTags[tagIndex];
            }
          }
          setEditedTags(selectedTagChanges);
          setTags(newTags);
          setDirtyTags(remainingDirtyTags);
        }

        if (assetIds.length > 0) {
          await queueTagSync(assetIds);
        }
      } catch (error) {
        setInfoMessages({
          type: "snackbar",
          message: `There was an issue saving tags: ${extractApiErrorMessage(error)}`,
          severity: "error",
          open: true,
          autoHideDuration: 10000,
        });
      }
    };

    const syncDisabledTags = tagsToSave.filter((t) => t.SyncToHub === false);
    const unresolvedParentTags = getTagsWithUnresolvedParent(
      tagsToSave,
      editedTags
    );
    if (syncDisabledTags.length > 0 || unresolvedParentTags.length > 0) {
      setConfirmationTitle("Confirm Save");
      setConfirmationText(
        <PreSaveWarningsContent tags={tagsToSave} allTags={editedTags} />
      );
      setConfirmationOnClose(() => async (result) => {
        setShowConfirmDeployDialog(false);
        if (result === true) {
          await proceedWithSave();
        }
      });
      setShowConfirmDeployDialog(true);
    } else {
      await proceedWithSave();
    }
  };

  const clearGrid = () => {
    setTags([]);
    setEditedTags([]);
    setDirtyTags(new Set());
    setSelectedTags(new Set());
  };

  const resetChanges = () => {
    setEditedTags(tags);
    setDirtyTags(new Set());
  };

  const convertXMLTagToCorrectFormat = (tag) => {
    let convertedTag = { SyncToHub: true, ...tag };
    // Field mappings
    // Type -> TagType
    // TagID -> TagIdentifier
    // DisplayName -> TagName
    // DataType -> TagDataType
    convertedTag.TagType = convertedTag.Type;
    convertedTag.TagIdentifier = convertedTag.TagID;
    convertedTag.TagName = convertedTag.DisplayName;
    convertedTag.IsActive = coerceToBoolean(convertedTag.IsEnabled);
    convertedTag.IsWriteable = coerceToBoolean(convertedTag.IsWriteable);
    convertedTag.AlarmTypeId = lookupNameToId(
      convertedTag.AlarmType,
      "TagAlarmType",
      lookupValues
    );
    convertedTag.Alarm2TypeId = lookupNameToId(
      convertedTag.Alarm2Type,
      "TagAlarmType",
      lookupValues
    );
    convertedTag.TagSourceTypeId = lookupNameToId(
      convertedTag.SourceType,
      "TagSourceType",
      lookupValues
    );
    convertedTag.TagTypeId = lookupNameToId(
      convertedTag.TagType,
      "TagType",
      lookupValues
    );
    convertedTag.DataTypeId = lookupNameToId(
      convertedTag.DataType,
      "TagDataType",
      lookupValues
    );
    convertedTag.AlarmArmingTypeId = lookupNameToId(
      convertedTag.AlarmArmingType,
      "AlarmArmingType",
      lookupValues
    );
    convertedTag.AlarmSeverityId = lookupNameToId(
      convertedTag.AlarmPriority,
      "AlarmSeverity",
      lookupValues
    );

    // Check DeviceParams format and adjust if needed
    convertedTag.DeviceParams = updateDeviceParamsFormat(
      convertedTag.DeviceParams,
      convertedTag.SourceType,
      convertedTag.ChannelNumber
    );
    delete convertedTag.Type;
    delete convertedTag.TagID;
    delete convertedTag.DisplayName;
    delete convertedTag.IsEnabled;
    return convertedTag;
  };

  const convertXMLTagsToCorrectFormat = (tags) => {
    return tags.map((tag) => convertXMLTagToCorrectFormat(tag));
  };

  // Parse and load tags from XML
  const loadTagsFromXML = (xmlString) => {
    try {
      const parser = new XMLParser({
        ignoreAttributes: false,
        attributeNamePrefix: "",
      });
      const parsedXML = parser.parse(xmlString);
      if (!parsedXML.WriteTagConfigRequest) {
        throw new Error("Incorrect XML format");
      }
      if (_.isArray(parsedXML.WriteTagConfigRequest)) {
        const convertedTags = convertXMLTagsToCorrectFormat(
          parsedXML.WriteTagConfigRequest
        );
        appendTagsToGrid(convertedTags);
      } else {
        const convertedTag = convertXMLTagToCorrectFormat(
          parsedXML.WriteTagConfigRequest
        );
        appendTagsToGrid([convertedTag]);
      }
    } catch (error) {
      console.error("Failed to load tags from XML:", error);
      setInfoMessages({
        type: "snackbar",
        message: `Failed to load tags from XML: ${extractApiErrorMessage(error)}`,
        severity: "error",
        open: true,
      });
    }
  };

  const convertTagToXMLFormat = (tag) => {
    let xmlTag = { ...tag };

    if (xmlTag.TagType !== undefined) {
      xmlTag.Type = xmlTag.TagType;
    }
    if (xmlTag.TagIdentifier !== undefined) {
      xmlTag.TagID = xmlTag.TagIdentifier;
    }
    if (xmlTag.TagName !== undefined) {
      xmlTag.DisplayName = xmlTag.TagName;
    }
    if (xmlTag.IsActive !== undefined) {
      xmlTag.IsEnabled = coerceToBoolean(xmlTag.IsActive);
    }
    if (xmlTag.IsWriteable !== undefined) {
      xmlTag.IsWriteable = coerceToBoolean(xmlTag.IsWriteable);
    }

    if (xmlTag.AlarmTypeId) {
      try {
        xmlTag.AlarmType = lookupIdToName(
          xmlTag.AlarmTypeId,
          "TagAlarmType",
          lookupValues
        );
      } catch (e) {
        console.warn(
          "Could not convert AlarmTypeId to name:",
          xmlTag.AlarmTypeId
        );
      }
      delete xmlTag.AlarmTypeId;
    }
    if (xmlTag.Alarm2TypeId) {
      try {
        xmlTag.Alarm2Type = lookupIdToName(
          xmlTag.Alarm2TypeId,
          "TagAlarmType",
          lookupValues
        );
      } catch (e) {
        console.warn(
          "Could not convert Alarm2TypeId to name:",
          xmlTag.Alarm2TypeId
        );
      }
      delete xmlTag.Alarm2TypeId;
    }
    if (xmlTag.SourceTypeId) {
      try {
        xmlTag.SourceType = lookupIdToName(
          xmlTag.SourceTypeId,
          "TagSourceType",
          lookupValues
        );
      } catch (e) {
        console.warn(
          "Could not convert SourceTypeId to name:",
          xmlTag.SourceTypeId
        );
      }
      delete xmlTag.SourceTypeId;
    }
    if (xmlTag.TagSourceTypeId) {
      try {
        xmlTag.SourceType = lookupIdToName(
          xmlTag.TagSourceTypeId,
          "TagSourceType",
          lookupValues
        );
      } catch (e) {
        console.warn(
          "Could not convert TagSourceTypeId to name:",
          xmlTag.TagSourceTypeId
        );
      }
      delete xmlTag.SourceTypeId;
    }
    if (xmlTag.TagTypeId) {
      try {
        xmlTag.Type = lookupIdToName(xmlTag.TagTypeId, "TagType", lookupValues);
      } catch (e) {
        console.warn("Could not convert TagTypeId to name:", xmlTag.TagTypeId);
      }
      delete xmlTag.TagTypeId;
    }
    if (xmlTag.DataTypeId) {
      try {
        xmlTag.DataType = lookupIdToName(
          xmlTag.DataTypeId,
          "TagDataType",
          lookupValues
        );
      } catch (e) {
        console.warn(
          "Could not convert DataTypeId to name:",
          xmlTag.DataTypeId
        );
      }
      delete xmlTag.DataTypeId;
    }

    delete xmlTag.TagType;
    delete xmlTag.TagIdentifier;
    delete xmlTag.TagName;
    delete xmlTag.IsActive;
    delete xmlTag.index;
    delete xmlTag.TagId;
    delete xmlTag.TCTagLayerId;
    delete xmlTag.AssetId;
    delete xmlTag.AssetNumber;
    delete xmlTag.AssetTagId;
    delete xmlTag.TagListName;

    return xmlTag;
  };

  const exportTagsToXML = () => {
    try {
      if (selectedTags.size === 0) {
        setInfoMessages({
          open: true,
          severity: "warning",
          message: "Please select at least one tag to export.",
        });
        return;
      }

      const selectedTagIndexes = Array.from(selectedTags);
      const tagsToExport = selectedTagIndexes.map((index) => editedTags[index]);

      const xmlTags = tagsToExport.map((tag) => convertTagToXMLFormat(tag));

      const xmlData = {
        WriteTagConfigRequest: xmlTags.length === 1 ? xmlTags[0] : xmlTags,
      };

      const builder = new XMLBuilder({
        ignoreAttributes: false,
        attributeNamePrefix: "",
        format: true,
        indentBy: "  ",
        suppressBooleanAttributes: false,
      });

      const xmlString = builder.build(xmlData);

      const blob = new Blob([xmlString], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `tags_export_${new Date().toISOString().split("T")[0]}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setInfoMessages({
        open: true,
        severity: "success",
        message: `Successfully exported ${xmlTags.length} tag(s) to XML.`,
      });
    } catch (e) {
      console.error(e);
      setInfoMessages({
        open: true,
        severity: "error",
        message: "Error exporting tags to XML.",
      });
    }
  };

  const dropHandler = (e) => {
    e.preventDefault();
    if (e.dataTransfer.items) {
      [...e.dataTransfer.items].forEach((dropItem, _) => {
        if (dropItem.kind === "file") {
          dropItem
            .getAsFile()
            .text()
            .then((xml) => {
              loadTagsFromXML(xml);
            });
        }
      });
    }
  };

  const dragOverHandler = (e) => {
    e.preventDefault();
  };

  const removeTagFromGroup = (tagIndex, groupName) => {
    const updatedTags = [...editedTags];
    const tag = updatedTags[tagIndex];
    const currentGroups = tag.GroupName ? tag.GroupName.split(";") : [];

    const filteredGroups = currentGroups.filter((g) => g !== groupName);
    updatedTags[tagIndex] = {
      ...tag,
      GroupName: filteredGroups.join(";"),
    };
    setEditedTags(updatedTags);
    setDirtyTags(new Set([...dirtyTags, tagIndex]));

    setInfoMessages({
      type: "snackbar",
      message: `Removed Tag "${tag.TagName}" from group "${groupName}".`,
      severity: "success",
      open: true,
    });
  };

  const addTagToGroup = (tagIndex, targetGroupName) => {
    const updatedTags = [...editedTags];
    const tag = updatedTags[tagIndex];
    const currentGroups = tag.GroupName ? tag.GroupName.split(";") : [];
    if (!currentGroups.includes(targetGroupName)) {
      currentGroups.push(targetGroupName);
      updatedTags[tagIndex] = {
        ...tag,
        GroupName: currentGroups.join(";"),
      };
      setEditedTags(updatedTags);
      setDirtyTags(new Set([...dirtyTags, tagIndex]));

      setInfoMessages({
        type: "snackbar",
        message: `Added Tag "${tag.TagName}" to group "${targetGroupName}".`,
        severity: "success",
        open: true,
      });
    }
  };

  const deleteGroup = (groupName) => {
    const updatedTags = [...editedTags];
    let indicesModified = [];
    let tagsModified = false;
    updatedTags.forEach((tag, index) => {
      if (tag.GroupName) {
        const currentGroups = tag.GroupName.split(";");
        if (currentGroups.includes(groupName)) {
          const filteredGroups = currentGroups.filter((g) => g !== groupName);
          updatedTags[index] = {
            ...tag,
            GroupName: filteredGroups.join(";"),
          };
          indicesModified.push(index);
          tagsModified = true;
        }
      }
    });

    if (tagsModified) {
      setEditedTags(updatedTags);
      setDirtyTags(new Set([...dirtyTags, ...indicesModified]));
      setInfoMessages({
        type: "snackbar",
        message: `Deleted group "${groupName}" and removed it from all Tags.`,
        severity: "success",
        open: true,
      });
    } else {
      setInfoMessages({
        type: "snackbar",
        message: `Deleted group "${groupName}".`,
        severity: "success",
        open: true,
      });
    }
  };

  const renameGroup = (oldGroupName, newGroupName) => {
    const updatedTags = [...editedTags];
    let tagsModified = false;
    let indicesModified = [];
    updatedTags.forEach((tag, index) => {
      if (tag.GroupName) {
        const currentGroups = tag.GroupName.split(";");
        if (currentGroups.includes(oldGroupName)) {
          const renamedGroups = currentGroups.map((g) =>
            g === oldGroupName ? newGroupName : g
          );
          updatedTags[index] = {
            ...tag,
            GroupName: renamedGroups.join(";"),
          };
          indicesModified.push(index);
          tagsModified = true;
        }
      }
    });
    if (tagsModified) {
      setEditedTags(updatedTags);
      setDirtyTags(new Set([...dirtyTags, ...indicesModified]));
      setInfoMessages({
        type: "snackbar",
        message: `Renamed group "${oldGroupName}" to "${newGroupName}" and updated all Tags.`,
        severity: "success",
        open: true,
      });
    } else {
      setInfoMessages({
        type: "snackbar",
        message: `Renamed group "${oldGroupName}" to "${newGroupName}".`,
        severity: "success",
        open: true,
      });
    }
  };

  const addMultipleTagsToGroup = (tagIndices, groupName) => {
    const updatedTags = [...editedTags];
    let tagsModified = false;
    let indicesModified = [];
    updatedTags.forEach((tag, index) => {
      if (tagIndices.includes(index)) {
        const currentGroups = tag.GroupName ? tag.GroupName.split(";") : [];
        if (!currentGroups.includes(groupName)) {
          currentGroups.push(groupName);
          updatedTags[index] = {
            ...tag,
            GroupName: currentGroups.join(";"),
          };
          indicesModified.push(index);
          tagsModified = true;
        }
      }
    });
    if (tagsModified) {
      setEditedTags(updatedTags);
      setDirtyTags(new Set([...dirtyTags, ...indicesModified]));
      setInfoMessages({
        type: "snackbar",
        message: `Added ${tagIndices.length} Tags to group "${groupName}".`,
        severity: "success",
        open: true,
      });
    }
  };

  return (
    <>
      <Backdrop
        open={loading}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}>
        <CircularProgress />
      </Backdrop>
      <Paper elevation={2} sx={{ p: 2, mt: 1 }}>
        <Typography gutterBottom variant='h5' component='div' color='primary'>
          Tag Configurator
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Typography variant='body1'>
              Create, edit, and deploy Tags from the tag configurator.
            </Typography>
          </Grid>
          <Grid container item xs={8} spacing={1} justifyContent={"flex-end"}>
            <Grid item>
              <Button
                variant='contained'
                color='primary'
                startIcon={<SettingsApplications />}
                onClick={() => setOpenManageTagListsModal(true)}>
                Manage Tag Lists
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant='contained'
                color='primary'
                startIcon={<DriveFolderUploadIcon />}
                onClick={() => setOpenSourceModal(true)}>
                Load From Asset
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant='contained'
                color='primary'
                startIcon={<DriveFolderUploadIcon />}
                onClick={() => setOpenXMLModal(true)}>
                Load From XML
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant='contained'
                color='primary'
                onClick={() => setShowTagComparisonDialog(true)}
                disabled={editedTags.length === 0}>
                Tag Comparison
              </Button>
            </Grid>
          </Grid>
          <Grid
            container
            item
            spacing={1}
            xs={12}
            justifyContent={"space-between"}>
            <Grid item xs={3}>
              <Button
                variant='outlined'
                startIcon={
                  currentView === "grid" ? <GroupIcon /> : <ViewListIcon />
                }
                onClick={() =>
                  setCurrentView(currentView === "grid" ? "groups" : "grid")
                }>
                {currentView === "grid" ? "Manage Tag Groups" : "View Grid"}
              </Button>
            </Grid>
            <Grid container item spacing={1} xs={9} justifyContent={"flex-end"}>
              <Grid item>
                <SelectCollisionMode
                  collisionMode={collisionMode}
                  setCollisionMode={setCollisionMode}
                  showCollisionInfo={() => {
                    collisionPrompt();
                  }}
                />
              </Grid>
              <Grid item>
                <SelectTagStandard
                  tagStandard={tagStandard}
                  setTagStandard={setTagStandard}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>
      <Paper sx={{ p: 2, my: 1, minHeight: 200 }}>
        {
          <>
            {currentView === "grid" ? (
              <div onDrop={dropHandler} onDragOver={dragOverHandler}>
                <TagDataGridWrapper
                  data={editedTags.map((tag, id) => {
                    return { ...tag, index: id };
                  })}
                  tags={tags.map((tag, id) => {
                    return { ...tag, index: id };
                  })}
                  dirtyTags={dirtyTags}
                  setDirtyTags={setDirtyTags}
                  setData={setEditedTags}
                  addNewEmptyTag={addNewEmptyTag}
                  deleteRow={deleteRow}
                  clearGrid={clearGrid}
                  duplicateTag={duplicateTag}
                  openTagDialog={openTagDialog}
                  resetChanges={resetChanges}
                  distro={distro}
                  setDistro={setDistro}
                  selectedTags={selectedTags}
                  setSelectedTags={setSelectedTags}
                  setInfoMessages={setInfoMessages}
                  tagStandardTags={tagStandardTags}
                  selectedColumns={selectedColumns}
                  setSelectedColumns={setSelectedColumns}
                  onBulkRemoveSelected={deleteSelectedRows}
                />
              </div>
            ) : (
              <GroupPane
                tags={editedTags}
                setInfoMessages={setInfoMessages}
                addTagToGroup={addTagToGroup}
                removeTagFromGroup={removeTagFromGroup}
                deleteGroup={deleteGroup}
                renameGroup={renameGroup}
                addMultipleTagsToGroup={addMultipleTagsToGroup}
              />
            )}
            {currentView === "grid" && (
              <>
                <Divider sx={{ my: 2 }} />
                <Stack direction={"row"} spacing={2} justifyContent={"right"}>
                  <Button
                    startIcon={<Download />}
                    onClick={exportTagsToXML}
                    variant='contained'
                    disabled={selectedTags.size === 0}>
                    Export to XML
                  </Button>
                  <Button
                    startIcon={<SettingsApplications />}
                    onClick={() => setBulkUpdateModalOpen(true)}
                    variant='contained'
                    disabled={selectedTags.size === 0}>
                    Bulk Update ({selectedTags.size})
                  </Button>
                  <Button
                    startIcon={<Settings />}
                    onClick={() => setTagListAppendOpen(true)}
                    variant='contained'>
                    Append to Tag List
                  </Button>
                  <Button
                    startIcon={<Settings />}
                    onClick={() => setTagListConfigOpen(true)}
                    variant='contained'>
                    Save New Tag List
                  </Button>
                  <Tooltip title={saveChangesTooltipText}>
                    <Button
                      variant='contained'
                      onClick={async () => {
                        await saveChanges();
                      }}
                      startIcon={<Save />}>
                      Save Changes
                    </Button>
                  </Tooltip>
                  <ConfigureTagList
                    open={tagListConfigOpen}
                    onClose={() => {
                      setTagListConfigOpen(false);
                      setShowDeployGrid(false);
                    }}
                    tagListInfo={newTagList}
                    setTagListInfo={setNewTagList}
                    setInfoMessages={setInfoMessages}
                    constructNewTagListFromCurrentGrid={
                      constructNewTagListFromCurrentGrid
                    }
                  />
                  <AppendToTagList
                    open={tagListAppendOpen}
                    onClose={() => {
                      setTagListAppendOpen(false);
                    }}
                    editedTags={editedTags}
                    selectedTags={selectedTags}
                  />
                </Stack>
              </>
            )}
          </>
        }
      </Paper>
      {editedTags.length > 0 && !newTagList.IsTagStandard && (
        <SourceSelection
          setShowDeployGrid={setShowDeployGrid}
          setAssetTargetFilter={setAssetTargetFilter}
          assetTargetFilter={assetTargetFilter}
          setTargetAssets={setTargetAssets}
          setLoading={setLoading}
          setInfoMessages={setInfoMessages}></SourceSelection>
      )}
      {showDeployGrid ? (
        <TagDeployer
          tags={tagsForDeploy}
          targets={targetAssets}
          setShowConfirmDeployDialog={setShowConfirmDeployDialog}
          setConfirmationOnClose={setConfirmationOnClose}
          setConfirmationTitle={setConfirmationTitle}
          setConfirmationText={setConfirmationText}
          setInfoMessages={setInfoMessages}
        />
      ) : (
        <></>
      )}
      <EditTabSourceModal
        open={openSourceModal}
        onClose={() => {
          setOpenSourceModal(false);
        }}
        setSourceFilter={setSourceFilter}
        sourceFilter={sourceFilter}
        collisionMode={collisionMode}
        collisionPrompt={collisionPrompt}
        setLoading={setLoading}
        appendTagsToGrid={appendTagsToGrid}
      />
      <LoadXMLModal
        open={openXMLModal}
        onClose={() => {
          setOpenXMLModal(false);
        }}
        loadXML={(xml) => {
          setLoading(true);
          loadTagsFromXML(xml);
          setLoading(false);
        }}
        setOpen={setOpenXMLModal}
      />
      <ManageTagListsModal
        open={openManageTagListsModal}
        onClose={() => setOpenManageTagListsModal(false)}
        setInfoMessages={setInfoMessages}
        onLoadTagLists={getTagLists}
      />
      {confirmationDialog}
      {dialogOpen && (
        <SharedTagDialog
          open={dialogOpen}
          setOpen={setDialogOpen}
          tag={dialogTag}
          setTag={setDialogTag}
          updateTag={updateTagFromEditor}
          setLoading={setLoading}
          setInfoMessages={setInfoMessages}
          dialogId={dialogId}
          distro={distro}
          existingTags={editedTags}
        />
      )}
      <BulkUpdateModal
        open={bulkUpdateModalOpen}
        onClose={() => setBulkUpdateModalOpen(false)}
        selectedTags={selectedTags}
        selectedTagRecords={Array.from(selectedTags).map(
          (index) => editedTags[index]
        )}
        onBulkUpdate={bulkUpdateTags}
        editedTags={editedTags}
      />
      <TagComparisonModal
        setUserTags={setEditedTags}
        userTags={editedTags}
        setShowTagComparisonDialog={setShowTagComparisonDialog}
        distro={distro}
        setDistro={setDistro}
        setDirtyTags={setDirtyTags}
        showTagComparisonDialog={showTagComparisonDialog}
        currentCollisionMode={collisionMode}
        currentSetCollisionMode={setCollisionMode}
        currentSelectedColumns={selectedColumns}
        currentSetSelectedColumns={setSelectedColumns}
      />
    </>
  );
}
